import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AgreementType, TimeEntry, TimeEntrySummaryBucket } from '@catalyst-psa/shared';
import {
  DEFAULT_PAGE_SIZE,
  RATE_SOURCE_EXPLANATIONS,
  RATE_SOURCE_LABELS,
  mondayOf,
} from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/date.js';
import { useBackNavigation } from '../../utils/navigation.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { ticketRefLabel } from '../tickets/reference.js';
import { MemberMultiFilter } from '../members/MemberMultiFilter.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import { savedString, savedStringArray, useFilterDefaults } from '../filters/useFilterDefaults.js';
import { TimeEntryFormModal } from './TimeEntryFormModal.js';
import { useTrackRecent } from '../quick-access/useTrackRecent.js';
import {
  useBillingClassOptions,
  useMemberOptions,
  useTimeEntry,
  useServiceCategoryOptions,
  useTimeEntries,
  useTimeEntriesSummary,
  useWriteOffTimeEntry,
} from './api.js';

/** Formats a Date as a local YYYY-MM-DD string (no timezone shift). */
function localDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** First and last day of the calendar month containing `date`. */
function monthRange(date: Date): { from: string; to: string } {
  const from = new Date(date.getFullYear(), date.getMonth(), 1);
  const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: localDateString(from), to: localDateString(to) };
}

/** Default date range: the current calendar month. */
function defaultDateRange(): { from: string; to: string } {
  return monthRange(new Date());
}

/** Shifts a `from` date by whole months and returns that month's full range. */
function shiftMonth(fromDate: string, months: number): { from: string; to: string } {
  const base = fromDate ? new Date(`${fromDate}T00:00:00`) : new Date();
  return monthRange(new Date(base.getFullYear(), base.getMonth() + months, 1));
}

/** Monday..Sunday week range containing `iso`. */
function weekRange(iso: string): { from: string; to: string } {
  const monday = mondayOf(iso);
  const end = new Date(`${monday}T00:00:00`);
  end.setDate(end.getDate() + 6);
  return { from: monday, to: localDateString(end) };
}

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Trims trailing zeros from an hours string for compact display. */
function formatHours(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return Number.isInteger(n) ? String(n) : Number(n.toFixed(2)).toString();
}

/** Billable share of the filtered hours, as a plain number string (no % sign). */
function percentBillable(billable: string, total: string): string {
  const t = Number(total);
  if (!Number.isFinite(t) || t === 0) return '0';
  return (((Number(billable) || 0) / t) * 100).toFixed(1);
}

/** Tooltip text describing who wrote an entry off and when. */
function writeOffAttribution(entry: TimeEntry): string {
  const who = entry.writtenOffByName ? ` by ${entry.writtenOffByName}` : '';
  const when = entry.writtenOffAt ? ` on ${formatDate(entry.writtenOffAt)}` : '';
  return `Written off${who}${when}`.trim();
}

/** One headline hours total above the breakdown cards. */
function TotalStat({
  label,
  value,
  isLoading,
  tone = 'text-slate-900',
  suffix = '',
}: {
  label: string;
  value: string | undefined;
  isLoading: boolean;
  tone?: string;
  suffix?: string;
}): JSX.Element {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      <span className={`text-2xl font-bold tabular-nums ${tone}`}>
        {value !== undefined ? `${formatHours(value)}${suffix}` : isLoading ? '…' : `0${suffix}`}
      </span>
    </div>
  );
}

/** One labelled hours-breakdown card in the summary panel. Rows toggle a filter. */
function SummaryCard({
  title,
  rows,
  isLoading,
  activeId,
  onSelect,
}: {
  title: string;
  rows: TimeEntrySummaryBucket[];
  isLoading: boolean;
  /** Id of the bucket currently driving a filter, so it can be highlighted. */
  activeId?: string;
  onSelect: (bucket: TimeEntrySummaryBucket) => void;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      {isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400">No hours.</p>
      ) : (
        <ul className="max-h-44 space-y-1 overflow-auto pr-1 text-sm">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onSelect(r)}
                className={`flex w-full items-baseline justify-between gap-3 rounded px-1 text-left hover:bg-brand-50 ${
                  activeId === r.id ? 'bg-brand-50 ring-1 ring-brand-300' : ''
                }`}
              >
                <span className="truncate text-slate-700">{r.name}</span>
                <span className="shrink-0 tabular-nums font-medium text-slate-900">
                  {formatHours(r.hours)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TimeEntriesPage(): JSX.Element {
  const { can, isAdmin, isTimeApprover, principal } = useAuth();
  const canWriteOff = isAdmin || isTimeApprover || can('time', 'delete');
  const backNav = useBackNavigation();
  const navigate = useNavigate();
  const rowLink = useRowLink();
  const currentMemberId = principal?.type === 'member' ? principal.id : '';

  const [page, setPage] = useState(1);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [agreementId, setAgreementId] = useState('');
  const [agreementType, setAgreementType] = useState<'' | AgreementType>('');
  const [agreementSubtypeId, setAgreementSubtypeId] = useState('');
  const [projectTypeId, setProjectTypeId] = useState('');
  const [serviceCategoryId, setServiceCategoryId] = useState('');
  const [billingClassId, setBillingClassId] = useState('');
  const [billable, setBillable] = useState<'' | 'true' | 'false'>('');
  const [notesInput, setNotesInput] = useState('');
  const [notes, setNotes] = useState('');
  const [fromDate, setFromDate] = useState(() => defaultDateRange().from);
  const [toDate, setToDate] = useState(() => defaultDateRange().to);
  // Whether the date range is a quick week/month window ('' = custom dates).
  const [rangeMode, setRangeMode] = useState<'' | 'week' | 'month'>('month');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(null);
  const { id: routeTimeEntryId } = useParams<{ id?: string }>();

  useTrackRecent(
    formOpen && editing
      ? {
          entity: 'timeEntry',
          id: editing.id,
          label: `${editing.workLabel ?? editing.ticketTitle ?? 'Time'} · ${editing.workDate}`,
          path: `/time-entries/${editing.id}`,
        }
      : null,
  );

  const pendingQuery = useTimeEntry(pendingOpenId);

  // Debounce the free-text Notes search so each keystroke doesn't refetch.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setNotes(notesInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [notesInput]);

  // Default the member filter to the logged-in member once the principal
  // resolves. Users can still change or clear it afterward. A saved filter
  // default (below) loads afterwards and overrides this seed.
  const didInitMember = useRef(false);
  useEffect(() => {
    if (didInitMember.current || !principal) {
      return;
    }
    didInitMember.current = true;
    if (currentMemberId) {
      setMemberIds([currentMemberId]);
    }
  }, [principal, currentMemberId]);

  const filterDefaults = useFilterDefaults('time-entries', (saved) => {
    didInitMember.current = true;
    const savedMembers = savedStringArray(saved, 'memberIds');
    if (savedMembers !== null) setMemberIds(savedMembers);
    const savedCustomer = savedString(saved, 'customerId');
    if (savedCustomer !== null) setCustomerId(savedCustomer);
    const savedAgreementType = savedString(saved, 'agreementType');
    if (savedAgreementType !== null) setAgreementType(savedAgreementType as '' | AgreementType);
    const savedSubtype = savedString(saved, 'agreementSubtypeId');
    if (savedSubtype !== null) setAgreementSubtypeId(savedSubtype);
    const savedProjectType = savedString(saved, 'projectServiceCategoryId');
    if (savedProjectType !== null) setProjectTypeId(savedProjectType);
    const savedCategory = savedString(saved, 'serviceCategoryId');
    if (savedCategory !== null) setServiceCategoryId(savedCategory);
    const savedClass = savedString(saved, 'billingClassId');
    if (savedClass !== null) setBillingClassId(savedClass);
    const savedBillable = savedString(saved, 'billable');
    if (savedBillable !== null) setBillable(savedBillable as '' | 'true' | 'false');
    // Saved range mode re-anchors to today: "week" = current week, "month" = current month.
    const savedRange = savedString(saved, 'rangeMode');
    if (savedRange === 'week') {
      const next = weekRange(localDateString(new Date()));
      setRangeMode('week');
      setFromDate(next.from);
      setToDate(next.to);
    } else if (savedRange === 'month') {
      const next = defaultDateRange();
      setRangeMode('month');
      setFromDate(next.from);
      setToDate(next.to);
    }
  });

  // Route-based deep-link: /time-entries/:id opens that time entry's standard modal.
  useEffect(() => {
    if (routeTimeEntryId) {
      setPendingOpenId(routeTimeEntryId);
    }
  }, [routeTimeEntryId]);

  useEffect(() => {
    if (pendingOpenId && pendingQuery.data) {
      setEditing(pendingQuery.data);
      setFormOpen(true);
      setPendingOpenId(null);
    }
  }, [pendingOpenId, pendingQuery.data]);

  const filters = {
    memberIds: memberIds.length > 0 ? memberIds : undefined,
    customerId: customerId || undefined,
    projectId: projectId || undefined,
    agreementId: agreementId || undefined,
    agreementType: agreementType || undefined,
    agreementSubtypeId: agreementSubtypeId || undefined,
    projectServiceCategoryId: projectTypeId || undefined,
    serviceCategoryId: serviceCategoryId || undefined,
    billingClassId: billingClassId || undefined,
    notes: notes || undefined,
    billable: billable === '' ? undefined : billable === 'true',
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  };

  const query = useTimeEntries({ page, pageSize: DEFAULT_PAGE_SIZE, ...filters });
  const summaryQuery = useTimeEntriesSummary(filters);
  const membersQuery = useMemberOptions();
  const categoriesQuery = useServiceCategoryOptions();
  const billingClassesQuery = useBillingClassOptions();
  const writeOffMutation = useWriteOffTimeEntry();

  /** Applies a summary-bucket filter, or clears it when the same bucket is clicked again. */
  const toggleFilter = (current: string, value: string, set: (next: string) => void): void => {
    setPage(1);
    set(current === value ? '' : value);
  };

  /** The "By Work" buckets are keyed by project OR agreement id; try both. */
  const selectWork = (bucket: TimeEntrySummaryBucket): void => {
    if (bucket.id === 'none') return;
    setPage(1);
    if (projectId === bucket.id || agreementId === bucket.id) {
      setProjectId('');
      setAgreementId('');
      return;
    }
    // A work label starting with '#' is a project (`#12 — Name`); anything else is an agreement.
    if (bucket.name.startsWith('#')) {
      setProjectId(bucket.id);
      setAgreementId('');
    } else {
      setAgreementId(bucket.id);
      setProjectId('');
    }
  };

  /** Week back/forward: shift the current week window, or snap to the week around today. */
  const stepWeek = (dir: -1 | 0 | 1): void => {
    setPage(1);
    const base =
      rangeMode === 'week' && fromDate
        ? (() => {
            const d = new Date(`${fromDate}T00:00:00`);
            d.setDate(d.getDate() + dir * 7);
            return localDateString(d);
          })()
        : localDateString(new Date());
    const next = weekRange(base);
    setRangeMode('week');
    setFromDate(next.from);
    setToDate(next.to);
  };

  /** Month back/forward: shift the current month window, or snap to the month around today. */
  const stepMonth = (dir: -1 | 0 | 1): void => {
    setPage(1);
    const base = rangeMode === 'month' && fromDate ? fromDate : localDateString(new Date());
    const next = shiftMonth(base, dir);
    setRangeMode('month');
    setFromDate(next.from);
    setToDate(next.to);
  };

  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of membersQuery.data ?? []) {
      map.set(m.id, m.displayName);
    }
    return map;
  }, [membersQuery.data]);

  const summary = summaryQuery.data;
  const entries = query.data?.data ?? [];
  const pageInfo = query.data?.page;
  const hasFilters = Boolean(
    memberIds.length > 0 ||
      customerId ||
      projectId ||
      agreementId ||
      agreementType ||
      agreementSubtypeId ||
      projectTypeId ||
      serviceCategoryId ||
      billingClassId ||
      billable ||
      notes ||
      fromDate ||
      toDate,
  );

  // Closing returns the address bar to the list when the modal was opened via a deep link.
  const closeForm = (): void => {
    setFormOpen(false);
    setEditing(null);
    if (routeTimeEntryId) navigate('/time-entries');
  };

  return (
    <div>
      <PageHeader
        title="Time Entries"
        description="Log labor against projects and agreements. Entries roll up into weekly timesheets."
        actions={
          backNav.canGoBack ? (
            <Button variant="secondary" onClick={backNav.goBack}>
              ← {backNav.backLabel}
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="mb-3 flex flex-wrap items-baseline gap-x-8 gap-y-2">
          <TotalStat
            label="Total Hours (filtered)"
            value={summary?.totalHours}
            isLoading={summaryQuery.isLoading}
          />
          <TotalStat
            label="Billable (filtered)"
            value={summary?.billableHours}
            isLoading={summaryQuery.isLoading}
            tone="text-emerald-700"
          />
          <TotalStat
            label="Non-Billable (filtered)"
            value={summary?.nonBillableHours}
            isLoading={summaryQuery.isLoading}
            tone="text-amber-700"
          />
          <TotalStat
            label="% Billable (filtered)"
            value={summary ? percentBillable(summary.billableHours, summary.totalHours) : undefined}
            isLoading={summaryQuery.isLoading}
            tone="text-emerald-700"
            suffix="%"
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <SummaryCard
            title="By Customer"
            rows={summary?.byCustomer ?? []}
            isLoading={summaryQuery.isLoading}
            activeId={customerId}
            onSelect={(b) => toggleFilter(customerId, b.id, setCustomerId)}
          />
          <SummaryCard
            title="By Member"
            rows={summary?.byMember ?? []}
            isLoading={summaryQuery.isLoading}
            activeId={memberIds.length === 1 ? memberIds[0]! : ''}
            onSelect={(b) => {
              setPage(1);
              setMemberIds(memberIds.length === 1 && memberIds[0] === b.id ? [] : [b.id]);
            }}
          />
          <SummaryCard
            title="By Work"
            rows={summary?.byWork ?? []}
            isLoading={summaryQuery.isLoading}
            activeId={projectId || agreementId}
            onSelect={selectWork}
          />
          <SummaryCard
            title="By Agreement Type"
            rows={summary?.byAgreementType ?? []}
            isLoading={summaryQuery.isLoading}
            activeId={agreementType}
            onSelect={(b) => {
              if (b.id === 'none') return;
              toggleFilter(agreementType, b.id, (next) =>
                setAgreementType(next as '' | AgreementType),
              );
            }}
          />
          <SummaryCard
            title="By Agreement SubType"
            rows={summary?.byAgreementSubtype ?? []}
            isLoading={summaryQuery.isLoading}
            activeId={agreementSubtypeId}
            onSelect={(b) => {
              if (b.id === 'none') return;
              toggleFilter(agreementSubtypeId, b.id, setAgreementSubtypeId);
            }}
          />
          <SummaryCard
            title="By Project Type"
            rows={summary?.byProjectType ?? []}
            isLoading={summaryQuery.isLoading}
            activeId={projectTypeId}
            onSelect={(b) => {
              if (b.id === 'none') return;
              toggleFilter(projectTypeId, b.id, setProjectTypeId);
            }}
          />
          <SummaryCard
            title="By Service Category"
            rows={summary?.byServiceCategory ?? []}
            isLoading={summaryQuery.isLoading}
            activeId={serviceCategoryId}
            onSelect={(b) => toggleFilter(serviceCategoryId, b.id, setServiceCategoryId)}
          />
          <SummaryCard
            title="By Billing Class"
            rows={summary?.byBillingClass ?? []}
            isLoading={summaryQuery.isLoading}
            activeId={billingClassId}
            onSelect={(b) => toggleFilter(billingClassId, b.id, setBillingClassId)}
          />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
          <MemberMultiFilter
            selected={memberIds}
            onChange={(next) => {
              setPage(1);
              setMemberIds(next);
            }}
            allLabel="All members"
          />
          <div className="w-56">
            <CustomerSelectField
              value={customerId}
              onChange={(value) => {
                setPage(1);
                setCustomerId(value);
              }}
              placeholder="All customers"
              allowClear
            />
          </div>
          <select
            value={serviceCategoryId}
            onChange={(e) => {
              setPage(1);
              setServiceCategoryId(e.target.value);
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All service categories</option>
            {(categoriesQuery.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={billingClassId}
            onChange={(e) => {
              setPage(1);
              setBillingClassId(e.target.value);
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All billing classes</option>
            {(billingClassesQuery.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <select
            value={billable}
            onChange={(e) => {
              setPage(1);
              setBillable(e.target.value as '' | 'true' | 'false');
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All billing</option>
            <option value="true">Billable</option>
            <option value="false">Non-billable</option>
          </select>
          <input
            type="search"
            value={notesInput}
            onChange={(e) => setNotesInput(e.target.value)}
            placeholder="Search Notes…"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <div className="flex items-center gap-1">
            <Button variant="secondary" onClick={() => stepWeek(-1)} title="Previous week">
              ‹
            </Button>
            <Button
              variant={rangeMode === 'week' ? 'primary' : 'secondary'}
              onClick={() => stepWeek(0)}
              title="Show one week at a time"
            >
              Week
            </Button>
            <Button variant="secondary" onClick={() => stepWeek(1)} title="Next week">
              ›
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="secondary" onClick={() => stepMonth(-1)} title="Previous month">
              ‹
            </Button>
            <Button
              variant={rangeMode === 'month' ? 'primary' : 'secondary'}
              onClick={() => stepMonth(0)}
              title="Show one month at a time"
            >
              Month
            </Button>
            <Button variant="secondary" onClick={() => stepMonth(1)} title="Next month">
              ›
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            From
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setPage(1);
                setRangeMode('');
                setFromDate(e.target.value);
              }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            To
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setPage(1);
                setRangeMode('');
                setToDate(e.target.value);
              }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </label>
          <FilterDefaultsButtons
            handle={filterDefaults}
            onSave={() =>
              filterDefaults.save({
                memberIds,
                customerId,
                agreementType,
                agreementSubtypeId,
                projectServiceCategoryId: projectTypeId,
                serviceCategoryId,
                billingClassId,
                billable,
                rangeMode,
              })
            }
          />
        </div>

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Member</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Ticket</th>
              <th className="px-4 py-3 font-medium">Work</th>
              <th className="px-4 py-3 font-medium">Service Category</th>
              <th className="px-4 py-3 font-medium">Billing Class</th>
              <th className="px-4 py-3 font-medium text-right">Hours</th>
              <th className="px-4 py-3 font-medium text-right">Rate</th>
              <th className="px-4 py-3 font-medium">Billing</th>
              <th className="px-4 py-3 font-medium">Notes</th>
              <th className="px-4 py-3 font-medium">Invoice</th>
              <th className="px-4 py-3 font-medium">Invoiced</th>
              <th className="px-4 py-3 font-medium">Written Off</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={14}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={entries.length === 0}
              emptyTitle="No time entries found"
              emptyDescription={
                hasFilters
                  ? 'Try a different filter or date range.'
                  : 'Time is logged from a timesheet or a ticket.'
              }
            >
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="cursor-pointer hover:bg-slate-50"
                  {...rowLink(`/time-entries/${entry.id}`)}
                >
                  <td className="px-4 py-3 tabular-nums text-slate-600">
                    {formatDate(entry.workDate)}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {entry.memberName ?? memberNames.get(entry.memberId) ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {entry.customerId ? (
                      <a
                        href={`/customers?returnCustomerId=${entry.customerId}&returnTab=details`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-brand-600 hover:text-brand-700 hover:underline"
                      >
                        {entry.customerName ?? 'Customer'}
                      </a>
                    ) : (
                      (entry.customerName ?? '—')
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {entry.ticketId ? (
                      <a
                        href={`/tickets?open=${entry.ticketId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                      >
                        {ticketRefLabel(entry.ticketReference, entry.ticketNumber)}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-900">
                    {entry.projectId || entry.agreementId ? (
                      <a
                        href={
                          entry.projectId
                            ? `/projects/${entry.projectId}`
                            : `/agreements/${entry.agreementId}`
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="text-brand-600 hover:text-brand-700 hover:underline"
                      >
                        {entry.workLabel ?? (entry.projectId ? 'Project' : 'Agreement')}
                      </a>
                    ) : (
                      (entry.workLabel ?? '—')
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{entry.serviceCategoryName ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{entry.billingClassName ?? '—'}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">
                    {entry.hours}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    <span
                      className="cursor-help underline decoration-dotted underline-offset-2"
                      title={`${RATE_SOURCE_LABELS[entry.rateSource]} — ${RATE_SOURCE_EXPLANATIONS[entry.rateSource]}`}
                    >
                      {currency.format(Number(entry.resolvedRate))}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={entry.billable ? 'green' : 'gray'}>
                      {entry.billable ? 'Billable' : 'Non-billable'}
                    </Badge>
                  </td>
                  <td
                    className="max-w-[16rem] truncate px-4 py-3 text-slate-600"
                    title={entry.notes ?? ''}
                  >
                    {entry.notes ? entry.notes : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {entry.invoiceId ? (
                      <a
                        href={`/invoices/${entry.invoiceId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                      >
                        {entry.invoiceNumber ?? 'View'}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-slate-600">
                    {formatDate(entry.invoicedAt)}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {canWriteOff ? (
                      <button
                        type="button"
                        onClick={() =>
                          writeOffMutation.mutate({
                            id: entry.id,
                            writtenOff: !entry.writtenOff,
                          })
                        }
                        disabled={
                          writeOffMutation.isPending && writeOffMutation.variables?.id === entry.id
                        }
                        title={
                          entry.writtenOff
                            ? `${writeOffAttribution(entry)}. Click to restore these hours`
                            : 'Click to write off these hours'
                        }
                        className="rounded disabled:opacity-50"
                      >
                        {entry.writtenOff ? (
                          <Badge tone="amber">Written off</Badge>
                        ) : (
                          <span className="text-xs text-slate-400 underline decoration-dotted underline-offset-2 hover:text-slate-600">
                            Write off
                          </span>
                        )}
                      </button>
                    ) : entry.writtenOff ? (
                      <span title={writeOffAttribution(entry)}>
                        <Badge tone="amber">Written off</Badge>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && entries.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <TimeEntryFormModal
        isOpen={formOpen}
        entry={editing}
        initialMemberId={currentMemberId || undefined}
        onClose={closeForm}
      />
    </div>
  );
}
